import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { CustomerType } from 'src/app/models/customer-type';
import { ICustomer } from 'src/app/models/interfaces/customer.interface';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { CustomerFormContext } from '../customer-form-context';

@Component({
    selector: 'app-customer-form',
    templateUrl: './customer-form.component.html',
    styleUrls: ['./customer-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        NgClass,
    ]
})
export class CustomerFormComponent extends ManagementFormComponent<ICustomer> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    public CustomerType = CustomerType;
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('customer')
    public dataInput: Required<ICustomer> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: CustomerFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<CustomerFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: CustomerFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('customerUpdated')
    public dataOutput: EventEmitter<ICustomer> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public customerType = [];

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });

        const typeKeys = Object.keys(CustomerType);
        const length = typeKeys.length / 2;

        for (let i = 1; i <= length; i++) {
            this.customerType.push({ id: +typeKeys[i - 1], value: typeKeys[i + length - 1] });
        }

        this.formGroup.get('faxNumber').setValidators([Validators.pattern(this.faxPattern)]);
        this.formGroup.get('phoneNumber').setValidators([Validators.pattern(this.phonePattern)]);
        this.formGroup.get('mobileNumber').setValidators([Validators.pattern(this.phonePattern)]);
        this.formGroup.get('csid').setValidators([Validators.pattern(this.phonePattern)]);
        this.formGroup.get('email').setValidators([Validators.required, Validators.pattern(this.emailPattern)]);
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<ICustomer> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            code: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            address1: new UntypedFormControl(null, Validators.required),
            address2: new UntypedFormControl(null),
            postalCode: new UntypedFormControl(null, Validators.required),
            city: new UntypedFormControl(null, Validators.required),
            country: new UntypedFormControl(null, Validators.required),
            siret: new UntypedFormControl(null),
            phoneNumber: new UntypedFormControl(null),
            mobileNumber: new UntypedFormControl(null),
            faxNumber: new UntypedFormControl(null),
            email: new UntypedFormControl(null, Validators.required),
            csid: new UntypedFormControl(null),
            type: new UntypedFormControl(null, Validators.required),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
